import React, { useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
}

const TOOLBAR_OPTIONS = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['blockquote', 'code-block'],
  ['link', 'image'],
  [{ align: [] }],
  ['clean'],
];

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start writing...',
  className = '',
  minHeight = '200px',
}) => {
  const modules = useMemo(
    () => ({
      toolbar: TOOLBAR_OPTIONS,
      clipboard: { matchVisual: false },
    }),
    []
  );

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'blockquote', 'code-block',
    'link', 'image',
    'align',
  ];

  const safeMinHeight = /^\d+(px|rem|em|%|vh|vw)$/.test(minHeight) ? minHeight : '200px';

  return (
    <div className={`rich-text-editor ${className}`}>
      <style>{`
        .rich-text-editor .ql-toolbar.ql-snow {
          display: flex;
          flex-wrap: wrap;
          gap: 0.25rem;
          align-items: center;
          padding: 0.75rem;
          border-top-left-radius: 12px;
          border-top-right-radius: 12px;
        }
        .rich-text-editor .ql-toolbar .ql-formats {
          margin-right: 0;
          display: flex;
          align-items: center;
          gap: 0.125rem;
        }
        .rich-text-editor .ql-toolbar button,
        .rich-text-editor .ql-toolbar .ql-picker {
          border-radius: 8px;
        }
        .rich-text-editor .ql-toolbar button {
          width: 2rem;
          height: 2rem;
          padding: 0;
        }
        .rich-text-editor .ql-toolbar button svg {
          width: 1rem;
          height: 1rem;
        }
        .rich-text-editor .ql-toolbar button:hover,
        .rich-text-editor .ql-toolbar button:focus-visible {
          background: rgba(148, 163, 184, 0.14);
        }
        .rich-text-editor .ql-toolbar .ql-stroke {
          stroke: #475569;
        }
        .rich-text-editor .ql-toolbar .ql-fill {
          fill: #475569;
        }
        .rich-text-editor .ql-toolbar .ql-picker-label {
          color: #475569;
        }
        .rich-text-editor .ql-container {
          min-height: ${safeMinHeight};
          font-size: 14px;
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
          border-color: #e2e8f0;
        }
        .rich-text-editor .ql-toolbar {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          border-color: #e2e8f0;
          background: #f8fafc;
        }
        .rich-text-editor .ql-editor {
          min-height: ${safeMinHeight};
          line-height: 1.6;
        }
        .rich-text-editor .ql-editor.ql-blank::before {
          color: #94a3b8;
          font-style: normal;
        }
        .dark .rich-text-editor .ql-container {
          border-color: #334155;
          background: #0f172a;
          color: #e2e8f0;
        }
        .dark .rich-text-editor .ql-toolbar {
          border-color: #334155;
          background: #1e293b;
        }
        .dark .rich-text-editor .ql-toolbar button:hover,
        .dark .rich-text-editor .ql-toolbar button:focus-visible {
          background: rgba(148, 163, 184, 0.18);
        }
        .dark .rich-text-editor .ql-toolbar .ql-stroke {
          stroke: #94a3b8;
        }
        .dark .rich-text-editor .ql-toolbar .ql-fill {
          fill: #94a3b8;
        }
        .dark .rich-text-editor .ql-toolbar .ql-picker-label {
          color: #94a3b8;
        }
        .rich-text-editor .ql-toolbar .ql-picker-options {
          z-index: 50;
        }
      `}</style>
      <ReactQuill
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
};

export default RichTextEditor;
